import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLang } from '../i18n/LanguageContext';
import ui from '../i18n/ui';

const navItems = [
  { path: '/', key: 'home' },
  { path: '/projects', key: 'projects' },
  { path: '/contact', key: 'contact' },
  { path: '/certifications', key: 'certifications' },
];

const mobileIcons = {
  home: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    </svg>
  ),
  projects: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  contact: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  certifications: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6" /><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  ),
};

export default function GlassNav() {
  const { pathname } = useLocation();
  const { lang, setLang } = useLang();
  const t = ui[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) => {
    if (path === '/') return pathname === '/';
    return pathname.startsWith(path);
  };

  return (
    <>
      {/* Desktop nav */}
      <nav
        className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 hidden md:flex items-center gap-1 px-2 py-1.5 rounded-full transition-all duration-300 ${
          scrolled ? 'shadow-glass-hover' : 'shadow-glass'
        }`}
        style={{
          backdropFilter: `blur(${scrolled ? '24px' : '20px'}) saturate(180%)`,
          WebkitBackdropFilter: `blur(${scrolled ? '24px' : '20px'}) saturate(180%)`,
          background: scrolled ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.55)',
          border: '1px solid rgba(255,255,255,0.25)',
        }}
      >
        {navItems.map((item) => (
          <Link
            key={item.key}
            to={item.path}
            className="relative px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 no-underline"
            style={{ color: isActive(item.path) ? '#1D1D1F' : '#6E6E73' }}
          >
            {isActive(item.path) && (
              <motion.span
                layoutId="nav-pill"
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 2px 8px rgba(10,132,255,0.15), inset 0 1px 0 rgba(255,255,255,0.9)',
                  border: '1px solid rgba(10,132,255,0.12)',
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10">{t[item.key]}</span>
          </Link>
        ))}

        {/* Language toggle */}
        <div className="ml-2 flex items-center rounded-full overflow-hidden border border-hairline bg-white/50">
          <button
            onClick={() => setLang('en')}
            className={`px-2.5 py-1 text-xs font-medium transition-all duration-200 border-none cursor-pointer ${
              lang === 'en' ? 'bg-ai-blue text-white' : 'bg-transparent text-secondary hover:text-primary'
            }`}
          >
            EN
          </button>
          <button
            onClick={() => setLang('es')}
            className={`px-2.5 py-1 text-xs font-medium transition-all duration-200 border-none cursor-pointer ${
              lang === 'es' ? 'bg-ai-blue text-white' : 'bg-transparent text-secondary hover:text-primary'
            }`}
          >
            ES
          </button>
        </div>
      </nav>

      {/* Mobile bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden flex items-center justify-around px-2 py-2 safe-bottom"
        style={{
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          background: 'rgba(255,255,255,0.75)',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
        }}
      >
        {navItems.map((item) => (
          <Link
            key={item.key}
            to={item.path}
            className="relative flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl no-underline transition-colors duration-200"
            style={{ color: isActive(item.path) ? '#0A84FF' : '#6E6E73' }}
          >
            {isActive(item.path) && (
              <motion.span
                layoutId="nav-pill-mobile"
                className="absolute inset-0 rounded-xl"
                style={{
                  background: 'rgba(10,132,255,0.08)',
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
            <span className="relative z-10">{mobileIcons[item.key]}</span>
            <span className="relative z-10 text-[10px] font-medium">{t[item.key]}</span>
          </Link>
        ))}

        {/* Mobile language toggle */}
        <button
          onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
          className="relative flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl text-secondary border-none bg-transparent cursor-pointer"
        >
          <span className="text-xs font-bold">{lang === 'en' ? 'ES' : 'EN'}</span>
          <span className="text-[10px] font-medium">{lang === 'en' ? 'Español' : 'English'}</span>
        </button>
      </nav>
    </>
  );
}
